/**
 * Where the CLI's committed config lives, and how it is read off disk.
 *
 * Two locations, one of them on its way out:
 *
 *  - `wayai-ws/wayai.yaml` — **the workspace manifest**, the one file a repo
 *    needs. Described below.
 *  - `.wayai.yaml` at the git root — the org binding's former home, now a
 *    deprecated read fallback (`repo-config.ts` owns the fallback rule; this
 *    module owns only where the file is).
 *
 * Both go through `loadYamlMapping`, so their read and parse semantics cannot
 * drift apart.
 *
 * ## The manifest
 *
 * It carries two unrelated kinds of declaration, and they are read with
 * DELIBERATELY DIFFERENT postures:
 *
 *  - `organization_id` / `organization_name` — the repo's org binding
 *    (`repo-config.ts`). **Hard.** Every request the CLI makes is org-scoped, so
 *    a missing or malformed org where one is required stops the command with an
 *    actionable error rather than guessing.
 *  - `default_hub` / `default_base` — which subtree a bare `pull`/`push` targets
 *    (`subtree-routing.ts`, rule 4). **Soft.** A malformed value is dropped with
 *    a warning and the remaining routing rules carry on: a broken default must
 *    not make every invocation in the repo fail before the subtree it would have
 *    selected can say anything useful.
 *
 * One file, two postures, and neither may leak into the other — a garbled
 * `default_base` must not break the org read, and an absent org must not silence
 * the defaults. This module owns only what they share: locating the file and
 * turning it into a document. Both postures are then applied by their own
 * reader, over the same `YamlMappingLoad`.
 *
 * The path is always the `wayai-ws/` one, never the legacy `workspace/` layout:
 * the manifest postdates that layout, exactly like `basesDir` in `layout.ts`.
 * The filename is deliberately NOT in `WAYAI_WORKSPACE_LAYOUT` — that descriptor
 * is the source of truth for the directory names the backend also has to know,
 * and this file is read by the CLI alone.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import * as yaml from 'js-yaml';
import { WAYAI_LAYOUT } from './layout.js';

export const WORKSPACE_MANIFEST_FILE = 'wayai.yaml';

/** `wayai-ws/wayai.yaml` — the spelling every user-facing message uses. */
export const WORKSPACE_MANIFEST_LABEL = `${WAYAI_LAYOUT.wsDir}/${WORKSPACE_MANIFEST_FILE}`;

/** Absolute path to the manifest for a git root. */
export function workspaceManifestPath(gitRoot: string): string {
  return path.join(gitRoot, WAYAI_LAYOUT.wsDir, WORKSPACE_MANIFEST_FILE);
}

/** The deprecated root-level org binding. Read as a fallback, never written. */
export const ROOT_CONFIG_FILE = '.wayai.yaml';

/** Absolute path to the deprecated root binding for a git root. */
export function rootConfigPath(gitRoot: string): string {
  return path.join(gitRoot, ROOT_CONFIG_FILE);
}

/**
 * What one read of a settings file found.
 *
 * `absent` and `malformed` are kept apart because the org posture must treat
 * them differently: an absent manifest legitimately falls through to the
 * deprecated root binding, while a manifest we could not parse must NOT — we do
 * not know whether it declared an organization, so honoring the fallback there
 * would be exactly the silent pick this design refuses. The defaults posture
 * collapses both to "no defaults" and neither reader has to know that.
 */
export type YamlMappingLoad =
  | { kind: 'absent' }
  | { kind: 'malformed'; reason: string }
  | { kind: 'ok'; doc: Record<string, unknown> };

/**
 * Load a YAML settings file as a mapping. Never throws — every failure mode is
 * a variant.
 *
 * An unreadable-but-present file (EACCES, EISDIR) is `malformed`, not `absent`:
 * the file IS there, and reporting it as missing would let the org read fall
 * through to the deprecated root file and bind a different organization without
 * saying so. An EMPTY file is `ok` with an empty document — a file that says
 * nothing is not the same as a file that says something garbled.
 *
 * Both committed config files go through this ONE reader. They were separate
 * copies that had already drifted on two points (whether a non-mapping document
 * is "says nothing" or "is garbled", and whether the parse error reaches the
 * user), and neither divergence was deliberate.
 */
export function loadYamlMapping(file: string): YamlMappingLoad {
  let raw: string;
  try {
    raw = fs.readFileSync(file, 'utf-8');
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'ENOENT') return { kind: 'absent' };
    return { kind: 'malformed', reason: err instanceof Error ? err.message : String(err) };
  }

  let doc: unknown;
  try {
    doc = yaml.load(raw);
  } catch (err) {
    return { kind: 'malformed', reason: err instanceof Error ? err.message : String(err) };
  }

  if (doc === undefined || doc === null) return { kind: 'ok', doc: {} };
  if (typeof doc !== 'object' || Array.isArray(doc)) {
    return { kind: 'malformed', reason: 'expected a YAML mapping of settings' };
  }
  return { kind: 'ok', doc: doc as Record<string, unknown> };
}

/** Load the workspace manifest. */
export function loadWorkspaceManifest(gitRoot: string): YamlMappingLoad {
  return loadYamlMapping(workspaceManifestPath(gitRoot));
}
